import { Box, Divider, FormControlLabel, Stack, Switch, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useRef, useState } from "react"
import { getSSOConfig } from "routes/settings/plugins/sso/controller/get-sso-config"
import { saveSSOConfig } from "routes/settings/plugins/sso/controller/save-sso-config"
import { invalidate } from "lib/graphql/cache"
import { errorSnackbar } from "lib/snackbar/error-snackbar"

export function IdpSettingsPanel({ onChanged }) {
    const { client } = useBoundContext()
    const clientId = client?.id
    const emptyConfig = useRef({})
    const config = getSSOConfig.useResults({ clientId }) ?? emptyConfig.current

    const [savingSSO, setSavingSSO] = useState(false)
    const [savingSCIM, setSavingSCIM] = useState(false)

    return (
        <Box sx={{ p: 3, maxWidth: 560 }}>
            <Typography variant="h6" gutterBottom>
                Identity Provider
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Integrate Facilities-iQ with your organisation's IDP for Single Sign-On and Automatic User Provisioning.
                These settings require working with your System Administrator.
            </Typography>

            <Stack divider={<Divider />} spacing={0}>
                <Box sx={{ py: 2.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ pr: 4 }}>
                            <Typography variant="subtitle2">Single Sign-On (SSO)</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                SAML 2.0 single sign-on via your identity provider.
                            </Typography>
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!config.enabled}
                                    onChange={handleToggleSSO}
                                    disabled={savingSSO}
                                    data-cy="toggle-sso-enabled"
                                />
                            }
                            label=""
                            sx={{ m: 0, flexShrink: 0 }}
                        />
                    </Stack>
                </Box>

                <Box sx={{ py: 2.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ pr: 4 }}>
                            <Typography variant="subtitle2">SCIM Provisioning</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Automated user provisioning and deprovisioning via SCIM 2.0.
                            </Typography>
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!config.scimEnabled}
                                    onChange={handleToggleSCIM}
                                    disabled={savingSCIM}
                                    data-cy="toggle-scim-enabled"
                                />
                            }
                            label=""
                            sx={{ m: 0, flexShrink: 0 }}
                        />
                    </Stack>
                </Box>
            </Stack>
        </Box>
    )

    async function handleToggleSSO(_, next) {
        setSavingSSO(true)
        try {
            await saveSSOConfig({ clientId, enabled: next })
            invalidate("getSSOConfig")
            onChanged?.()
        } catch (err) {
            errorSnackbar(err?.message || "Failed to update SSO setting")
        } finally {
            setSavingSSO(false)
        }
    }

    async function handleToggleSCIM(_, next) {
        setSavingSCIM(true)
        try {
            await saveSSOConfig({ clientId, scimEnabled: next })
            invalidate("getSSOConfig")
            onChanged?.()
        } catch (err) {
            errorSnackbar(err?.message || "Failed to update SCIM setting")
        } finally {
            setSavingSCIM(false)
        }
    }
}
